import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkle, PaperPlaneTilt, X, ArrowClockwise } from "@phosphor-icons/react";
import useCopilotChat from "@/hooks/useCopilotChat";

const SUGGESTED = [
  "Show me overdue invoices",
  "How much did we spend last 30 days?",
  "Summarise open service tickets",
  "Which vendors have pending POs?",
  "What's our net profit this quarter?",
];

/**
 * AICopilotDrawer — right-side chat drawer that keeps context and lets the
 * user hold multi-turn conversations with the ERP. Toggled via Cmd/Ctrl+/.
 *
 * State + persistence logic lives in `useCopilotChat` (iter47 refactor drops
 * cyclomatic complexity from 22 → 6).
 *
 * NOTE — persistence: chat history is stored in `sessionStorage` (per-tab,
 * cleared on tab close) rather than `localStorage`. Non-sensitive
 * conversational data — no auth tokens.
 */
function EmptyState({ send }) {
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-slate-500 leading-relaxed">
        Ask about invoices, POs, purchases, projects, tickets, or profitability.
        I read the same numbers your dashboards do.
      </div>
      <div className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-400 mt-3">Try</div>
      {SUGGESTED.map((s) => (
        <button
          key={s}
          onClick={() => send(s)}
          className="w-full text-left px-3 py-2 bg-white border border-slate-200 rounded-sm text-[12.5px] text-slate-700 hover:border-[#EA580C] hover:bg-orange-50 transition-colors"
        >{s}</button>
      ))}
    </div>
  );
}

function Message({ m }) {
  return (
    <div className={`text-[13px] leading-relaxed ${m.role === "user" ? "text-right" : ""}`}>
      <div className={`inline-block max-w-[90%] px-3 py-2 rounded-sm ${m.role === "user" ? "bg-[#EA580C] text-white" : "bg-white border border-slate-200 text-slate-800"} whitespace-pre-wrap text-left`}>
        {m.content}
      </div>
    </div>
  );
}

export default function AICopilotDrawer({ open, onClose }) {
  const c = useCopilotChat({ open });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[998] bg-black/20 backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.aside
            data-testid="copilot-drawer"
            initial={{ x: 380 }} animate={{ x: 0 }} exit={{ x: 380 }}
            transition={{ duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[999] w-[380px] max-w-[92vw] bg-white border-l border-slate-200 flex flex-col shadow-2xl"
          >
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-orange-50 to-white">
              <Sparkle size={16} weight="fill" className="text-[#EA580C]" />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-slate-800">Celsius Copilot</div>
                <div className="text-[10.5px] text-slate-500 tracking-wider uppercase">AI · reads live data</div>
              </div>
              <button onClick={c.clear} title="Clear conversation" className="text-slate-400 hover:text-slate-700 p-1" data-testid="copilot-clear"><ArrowClockwise size={14} /></button>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="copilot-close"><X size={16} /></button>
            </header>

            <div ref={c.scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#FAF9F8] min-h-0">
              {c.messages.length === 0 && <EmptyState send={c.send} />}
              {c.messages.map((m) => <Message key={m.id} m={m} />)}
              {c.busy && (
                <div className="text-[12px] text-slate-500 flex items-center gap-1.5">
                  <Sparkle size={11} className="text-[#EA580C] animate-pulse" /> thinking…
                </div>
              )}
            </div>

            <footer className="p-3 border-t border-slate-200 bg-white flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={c.inputRef}
                  rows={2}
                  value={c.input}
                  onChange={(e) => c.setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); c.send(); } }}
                  placeholder="Ask anything…"
                  className="flex-1 resize-none rounded-sm border border-slate-300 px-3 py-2 text-[13px] outline-none focus:border-[#EA580C]"
                  data-testid="copilot-input"
                />
                <button
                  onClick={() => c.send()}
                  disabled={c.busy || !c.input.trim()}
                  data-testid="copilot-send"
                  className="p-2 bg-[#EA580C] text-white rounded-sm disabled:opacity-40 hover:bg-[#c2410c] transition-colors"
                ><PaperPlaneTilt size={16} weight="fill" /></button>
              </div>
              <div className="mt-1.5 text-[10px] text-slate-400 tracking-wide">Enter to send · Shift+Enter for newline · Cmd+/ to toggle</div>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
